import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiPhone, FiMail } from 'react-icons/fi';
import { FaFacebookF, FaInstagram, FaTwitter, FaYoutube, FaLinkedinIn, FaTiktok, FaCcVisa, FaCcMastercard, FaRegCreditCard } from 'react-icons/fa';
import { useState, useEffect } from 'react';
import axios from '../utils/axios';

let settingsCache = null;
let settingsCacheTimestamp = null;
const SETTINGS_CACHE_DURATION = 5 * 60 * 1000;

const Footer = () => {
    const { t } = useTranslation();
    const [settings, setSettings] = useState(settingsCache || {
        siteName: 'M A S H O W',
        socialMedia: { instagram: 'https://instagram.com/mashowaccessories' },
        contactPhone: '+90 (850) 000 00 00',
        contactEmail: 'info@mashow.com.tr'
    });

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const now = Date.now();
            if (settingsCache && settingsCacheTimestamp && (now - settingsCacheTimestamp) < SETTINGS_CACHE_DURATION) {
                setSettings(settingsCache);
                return;
            }
            const { data } = await axios.get('/settings');
            setSettings(data);
            settingsCache = data;
            settingsCacheTimestamp = now;
        } catch (error) {
            console.error('Ayarlar yüklenemedi');
        }
    };

    const getSocialIcon = (platform) => {
        const icons = {
            facebook: FaFacebookF,
            instagram: FaInstagram,
            twitter: FaTwitter,
            youtube: FaYoutube,
            linkedin: FaLinkedinIn,
            tiktok: FaTiktok
        };
        return icons[platform] || null;
    };

    const formatUrl = (url) => {
        if (!url) return '';
        if (url.startsWith('http://') || url.startsWith('https://')) return url;
        return `https://${url}`;
    };

    return (
        <footer className="bg-[#151515] text-[#a0a0a0] font-light border-t border-[#222222]">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16 text-center md:text-left">
                    
                    {/* Column 1: Brand Info & Socials */}
                    <div className="flex flex-col items-center md:items-start min-h-[260px]">
                        {/* Logo Text / Image */}
                        <div className="mb-6 flex flex-col items-center md:items-start">
                            <span className="text-white text-2xl font-serif tracking-[0.2em] uppercase">
                                {settings?.siteName || 'M A S H O W'}
                            </span>
                            <span className="block text-[#45b59c] text-[9px] font-bold tracking-[0.3em] uppercase mt-2">
                                ZARAFET VE ŞIKLIK
                            </span>
                        </div>
                        
                        <p className="text-xs leading-loose mb-8 text-center md:text-left">
                            Yılların deneyimi ve ustalığıyla işlenen her mücevher, bir hikaye anlatır. Türkiye'nin en seçkin lüks takı markası.
                        </p>

                        {/* Social Icons */}
                        {settings?.socialMedia && Object.values(settings.socialMedia).some(url => url) && (
                            <div className="flex justify-center md:justify-start gap-4 mb-8">
                                {Object.entries(settings.socialMedia).map(([platform, url]) => {
                                    if (!url) return null;
                                    const Icon = getSocialIcon(platform);
                                    if (!Icon) return null;
                                    return (
                                        <a key={platform} href={formatUrl(url)} target="_blank" rel="noopener noreferrer" 
                                           className="w-10 h-10 border border-[#333333] flex items-center justify-center text-white hover:border-[#45b59c] hover:text-[#45b59c] transition-colors"
                                           aria-label={platform}>
                                            <Icon size={14} />
                                        </a>
                                    );
                                })}
                            </div>
                        )}

                        {/* Contact Info */}
                        <div className="space-y-3 text-[11px] tracking-wider text-white w-full">
                            {settings?.contactPhone && (
                                <p className="flex items-center justify-center md:justify-start gap-3">
                                    <FiPhone className="text-[#45b59c] text-sm" /> 
                                    {settings.contactPhone}
                                </p>
                            )}
                            {settings?.contactEmail && (
                                <p className="flex items-center justify-center md:justify-start gap-3">
                                    <FiMail className="text-[#45b59c] text-sm" /> 
                                    {settings.contactEmail}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Column 2: KURUMSAL */}
                    <div className="flex flex-col items-center md:items-start">
                        <h2 className="text-white text-[11px] font-bold mb-6 tracking-[0.15em] uppercase">
                            KURUMSAL
                        </h2>
                        <ul className="space-y-4 text-xs text-center md:text-left">
                            <li><Link to="/order-tracking" className="hover:text-white transition-colors">{t('footer.orderTracking') || 'Sipariş Takibi'}</Link></li>
                            <li><Link to="/contact" className="hover:text-white transition-colors">{t('footer.contact') || 'İletişim'}</Link></li>
                            <li><Link to="/returns" className="hover:text-white transition-colors">{t('footer.returns') || 'İade Politikası'}</Link></li>
                            <li><Link to="/faq" className="hover:text-white transition-colors">{t('footer.faq') || 'S.S.S.'}</Link></li>
                            <li><Link to="/payment-options" className="hover:text-white transition-colors">{t('footer.paymentOptions') || 'Ödeme Seçenekleri'}</Link></li>
                        </ul>
                    </div>

                    {/* Column 3: HİZMETLERİMİZ */}
                    <div className="flex flex-col items-center md:items-start">
                        <h2 className="text-white text-[11px] font-bold mb-6 tracking-[0.15em] uppercase">
                            HİZMETLERİMİZ
                        </h2>
                        <ul className="space-y-4 text-xs text-center md:text-left">
                            <li><Link to="/about" className="hover:text-white transition-colors">{t('footer.aboutUs') || 'Hakkımızda'}</Link></li>
                            <li><Link to="/stores" className="hover:text-white transition-colors">{t('nav.stores') || 'Mağazalarımız'}</Link></li>
                            <li><Link to="/site-map" className="hover:text-white transition-colors">{t('footer.siteMap') || 'Site Haritası'}</Link></li>
                            <li><Link to="/careers" className="hover:text-white transition-colors">Kariyer</Link></li>
                        </ul>
                    </div>

                    {/* Column 4: YASAL */}
                    <div className="flex flex-col items-center md:items-start">
                        <h2 className="text-white text-[11px] font-bold mb-6 tracking-[0.15em] uppercase">
                            YASAL
                        </h2>
                        <ul className="space-y-4 text-xs text-center md:text-left">
                            <li><Link to="/kvkk" className="hover:text-white transition-colors">{t('footer.privacy') || 'Gizlilik Politikası'}</Link></li>
                            <li><Link to="/terms" className="hover:text-white transition-colors">{t('footer.terms') || 'Kullanım Koşulları'}</Link></li>
                            <li><Link to="/cookie-policy" className="hover:text-white transition-colors">{t('footer.cookiePolicy') || 'Çerez Politikası'}</Link></li>
                            <li><Link to="/distance-sales-agreement" className="hover:text-white transition-colors">{t('footer.distanceSales') || 'Satış Sözleşmesi'}</Link></li>
                        </ul>
                    </div>

                </div>

                {/* Section 1: QR Badges Row */}
                <div className="border-t border-[#222222] mt-16 pt-10 flex flex-wrap items-center justify-center gap-6 lg:gap-10">
                    {/* ETBİS QR Code Badge */}
                    <a 
                        href="https://etbis.ticaret.gov.tr/tr/SiteSorgulamaSonuc?siteId=7d6b2614-66e2-4ade-88d8-e864b9ba0235" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-4 px-5 py-3 bg-[#181818] border border-[#2d2d2d] rounded-2xl hover:border-[#45b59c] transition-all group shadow-md"
                        title="ETBİS Doğrulama ve Site Sorgulama"
                    >
                        <img 
                            src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https%3A%2F%2Fetbis.ticaret.gov.tr%2Ftr%2FSiteSorgulamaSonuc%3FsiteId%3D7d6b2614-66e2-4ade-88d8-e864b9ba0235" 
                            alt="ETBİS Karekod" 
                            className="w-20 h-20 bg-white p-1.5 rounded-xl object-contain shadow-xs group-hover:scale-105 transition-transform"
                        />
                        <div className="text-left">
                            <span className="block text-xs font-black text-white tracking-wider uppercase group-hover:text-[#45b59c] transition-colors leading-snug">
                                ETBİS'E KAYITLIDIR
                            </span>
                            <span className="block text-[10px] text-gray-400 font-medium mt-0.5">T.C. Ticaret Bakanlığı</span>
                            <span className="inline-block text-[9px] text-[#45b59c] font-bold tracking-widest uppercase mt-1.5 bg-[#45b59c]/10 px-2.5 py-0.5 rounded-md">Doğrulanmış Site</span>
                        </div>
                    </a>

                    {/* Google Review QR Code Badge */}
                    <div className="flex items-center gap-4 px-5 py-3 bg-[#181818] border border-[#2d2d2d] rounded-2xl hover:border-[#4285F4] transition-all group shadow-md">
                        <img 
                            src="/google-review-qr.png" 
                            alt="Google Yorum Yap QR" 
                            className="w-20 h-20 bg-white p-1.5 rounded-xl object-contain shadow-xs group-hover:scale-105 transition-transform"
                        />
                        <div className="text-left">
                            <span className="block text-xs font-black text-white tracking-wider uppercase group-hover:text-[#4285F4] transition-colors leading-snug">
                                GOOGLE YORUMLARI
                            </span>
                            <span className="block text-[10px] text-gray-400 font-medium mt-0.5">Bizi Google'da Değerlendirin</span>
                            <span className="inline-flex items-center gap-1 text-[9px] text-[#4285F4] font-bold tracking-widest uppercase mt-1.5 bg-[#4285F4]/10 px-2.5 py-0.5 rounded-md">
                                <span>⭐ Yorum & Puan Verin</span>
                            </span>
                        </div>
                    </div>
                </div>

                {/* Section 2: Official Company Legal Details */}
                <div className="mt-8 pt-6 border-t border-[#222222]/60 text-center space-y-1.5">
                    <p className="text-xs font-bold text-gray-300 tracking-wider uppercase">
                        MASHOW KUYUMCULUK BİLİŞİM YAZILIM VE DANIŞMANLIK TİCARET LİMİTED ŞİRKETİ
                    </p>
                    <p className="text-[11px] text-gray-400">
                        <span className="text-gray-500">KEP Adresi:</span>{' '}
                        <span className="font-mono text-gray-200 font-semibold select-all">mashowkuyumculuk@hs01.kep.tr</span>
                    </p>
                </div>

                {/* Section 3: Copyright & Payment Systems Bottom Bar */}
                <div className="mt-6 pt-6 border-t border-[#222222]/40 flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left">
                    <p className="text-[10px] tracking-widest uppercase text-gray-500">
                        © {new Date().getFullYear()} {settings?.siteName || 'MASHOW'}. TÜM HAKLARI SAKLIDIR.
                    </p>
                    <div className="flex items-center gap-3 text-white/50">
                        <FaCcVisa className="text-2xl hover:text-white transition-colors" />
                        <FaCcMastercard className="text-2xl hover:text-white transition-colors" />
                        <span className="text-[10px] font-bold tracking-widest uppercase text-white/70 ml-1">
                            GÜVENLİ ÖDEME
                        </span>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
